import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useRealtimeEvent } from '../contexts/RealtimeContext';

export default function BookingChat({ bookingId, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [pollInterval, setPollInterval] = useState(10000);
  const [lastMessageTime, setLastMessageTime] = useState(null);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initial fetch + mark as read
  useEffect(() => {
    async function fetchMessages() {
      try {
        setLoading(true);
        const res = await api.get(`/bookings/${bookingId}/messages?page=1`);
        const data = res.data;
        setMessages(data.messages || []);
        setError(null);

        // Check if there are older messages
        if (data.pagination && data.pagination.totalPages > 1) {
          setHasOlderMessages(true);
          setCurrentPage(1);
        }

        // Mark as read
        try {
          await api.post(`/bookings/${bookingId}/messages/mark-read`);
        } catch {
          // Non-blocking
        }

        setLastMessageTime(Date.now());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchMessages();
  }, [bookingId]);

  // Load older messages
  async function loadOlderMessages() {
    if (loadingOlder || !hasOlderMessages) return;
    setLoadingOlder(true);
    try {
      const nextPage = currentPage + 1;
      const res = await api.get(`/bookings/${bookingId}/messages?page=${nextPage}`);
      const data = res.data;
      const olderMessages = data.messages || [];

      // Preserve scroll position
      const container = messagesContainerRef.current;
      const prevScrollHeight = container?.scrollHeight || 0;

      setMessages((prev) => [...olderMessages, ...prev]);
      setCurrentPage(nextPage);
      setHasOlderMessages(nextPage < (data.pagination?.totalPages || 1));

      // Restore scroll position after prepending
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight;
        }
      });
    } catch {
      // Silent fail
    } finally {
      setLoadingOlder(false);
    }
  }

  // Polling with adaptive interval — fetches latest page only
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await api.get(`/bookings/${bookingId}/messages?page=1`);
        const newMessages = res.data.messages || [];

        // Check if we have new messages — compare last message ID
        const lastNew = newMessages[newMessages.length - 1];
        const lastCurrent = messages[messages.length - 1];
        const hasNew = lastNew && (!lastCurrent || lastNew.id !== lastCurrent.id);
        if (hasNew) {
          setMessages(newMessages);
          setLastMessageTime(Date.now());
          setPollInterval(10000); // Reset to 10s on new message
          // Mark as read
          try {
            await api.post(`/bookings/${bookingId}/messages/mark-read`);
          } catch {
            // Non-blocking
          }
        } else if (lastMessageTime && Date.now() - lastMessageTime > 5 * 60 * 1000) {
          // No new messages for 5 min -> 60s
          setPollInterval(60000);
        } else if (lastMessageTime && Date.now() - lastMessageTime > 2 * 60 * 1000) {
          // No new messages for 2 min -> 30s
          setPollInterval(30000);
        }
      } catch {
        // Silent fail
      }
    };

    pollTimerRef.current = setInterval(poll, pollInterval);
    return () => clearInterval(pollTimerRef.current);
  }, [bookingId, messages, lastMessageTime, pollInterval]);

  // Reset inactivity timer on poll interval change
  useEffect(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
  }, [pollInterval]);

  // Realtime: append incoming messages instantly (deduped by id)
  useRealtimeEvent('message:new', (data) => {
    if (!data || data.bookingId !== bookingId || !data.message) return;
    setMessages((prev) => {
      if (prev.some((m) => m.id === data.message.id)) return prev;
      return [...prev, data.message];
    });
    setLastMessageTime(Date.now());
    api.post(`/bookings/${bookingId}/messages/mark-read`).catch(() => {});
  });

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    setSending(true);
    try {
      const res = await api.post(`/bookings/${bookingId}/messages`, {
        content: messageText.trim(),
      });
      setMessages([...messages, res.data.message]);
      setMessageText('');
      setLastMessageTime(Date.now());
      setPollInterval(10000); // Reset to 10s on new message
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    // Less than 24 hours -> relative
    if (diffHours < 24) {
      const rtf = new Intl.RelativeTimeFormat('fr-FR', { numeric: 'auto' });
      if (diffMins < 60) {
        return rtf.format(-diffMins, 'minute');
      }
      return rtf.format(-diffHours, 'hour');
    }

    // Otherwise -> absolute date
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh] min-h-[300px] max-h-[600px] th-bg-surface">
        <div className="text-sm th-text-muted font-mono">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[50vh] min-h-[300px] max-h-[600px] th-bg-surface">
        <div className="text-sm text-status-rejected font-mono">Erreur: {error}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[50vh] min-h-[300px] max-h-[600px] border th-border th-bg-surface">
      {/* Messages list */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Load older messages button */}
        {hasOlderMessages && (
          <div className="text-center mb-2">
            <button
              onClick={loadOlderMessages}
              disabled={loadingOlder}
              className="text-xs font-mono th-text-muted hover:text-accent transition-colors duration-150 disabled:opacity-50"
            >
              {loadingOlder ? 'Chargement...' : '↑ Messages précédents'}
            </button>
          </div>
        )}
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm th-text-muted font-mono text-center">
              Aucun message. Envoyez le premier !
            </p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.senderId === currentUserId;
            return (
              <div
                key={msg.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs px-3 py-2 rounded-sm ${
                    isOwn
                      ? 'bg-accent/20 text-accent'
                      : 'th-bg text-current'
                  }`}
                >
                  {!isOwn && (
                    <p className="text-xs th-text-secondary font-mono mb-1">
                      {msg.senderDisplayName || 'Utilisateur'}
                    </p>
                  )}
                  <p className="text-sm break-words font-sans">{msg.content}</p>
                  <p className="text-xs th-text-muted font-mono mt-1 text-right">
                    {formatDate(msg.createdAt)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={handleSendMessage}
        className="border-t th-border p-4 flex gap-2"
      >
        <textarea
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Votre message..."
          rows={messageText.split('\n').length > 1 ? 3 : 1}
          className="th-input flex-1 resize-none font-sans text-sm min-h-[40px] max-h-[120px]"
        />
        <button
          type="submit"
          disabled={sending || !messageText.trim()}
          className="th-btn-accent px-4 py-2 h-[40px] disabled:opacity-50"
        >
          {sending ? '...' : 'Envoyer'}
        </button>
      </form>
    </div>
  );
}
